import React, { useContext, useState } from "react"
import { FirebaseContext } from "../Firebase"
import { getOrgOptions } from "../organizationOptions/organizationOptions"

/**
 * Parses the email credentials of the current user. The user's school is
 * determined using their corresponding memberships doc. All outputs are undefined
 * until FirebaseContext loads
 * @returns schoolLoginProvider - primary AuthProvider used by current user's school
 * @returns reauthProvider - an AuthProvider that can be used to reauthenticate the user using their current credentials
 * @returns email - the primary email of the current user
 * @returns personalEmail - if the school has school-specific emails, this is the personal email connected to the user's account (if it exists)
 * @returns schoolEmail - if the school has school-specific emails, this is the school email connected to the user's account (if it exists)
 * @returns schoolEmailNeedsConfirmation - if the user's school email needs to be re-confirmed (for example, because they reset their password)
 */
const useUserEmailCredentials = () => {
  const { firebase, user, profile } = useContext(FirebaseContext)
  const orgOptions = getOrgOptions(user?.membership?.schcode)

  if (!firebase?.auth?.currentUser || !orgOptions?.sso_methods?.length) {
    return {
      email: firebase?.auth?.currentUser?.email
    }
  }

  const authUser = firebase.auth.currentUser
  const orgSSOMethods = orgOptions.sso_methods
  const orgEmailSubdomains = orgOptions.emails || []
  const orgSpecificEmailsPossible = orgSSOMethods[0] !== "email"
                                    && orgEmailSubdomains.length > 0
  
  let email = authUser.email
  let personalEmail = ""
  let schoolEmail = ""
  let schoolEmailNeedsConfirmation = false
  if (orgSpecificEmailsPossible) {
    const secondaryProvider = authUser.providerData.find(p => p.email !== email)
    if (secondaryProvider) {
      personalEmail = email
      schoolEmail = secondaryProvider.email
    } else if (profile?.schoolEmail) {
      personalEmail = email
      schoolEmail = profile.schoolEmail
      schoolEmailNeedsConfirmation = true
    } else {
      const primaryEmailSubdomain = email.split("@")[1]
      if (orgEmailSubdomains.includes(`@${primaryEmailSubdomain}`)) {
        schoolEmail = email
      } else {
        personalEmail = email
      }
    }
  }

  const primarySchoolProvider = orgSSOMethods[0]
  let schoolLoginProvider = null
  switch (primarySchoolProvider) {
    case "google":
      schoolLoginProvider = new firebase.app.auth.GoogleAuthProvider()
      schoolLoginProvider.setCustomParameters({
        prompt: "consent",
      })
      break
    case "microsoft":
      schoolLoginProvider = new firebase.app.auth.OAuthProvider("microsoft.com")
      schoolLoginProvider.setCustomParameters({
        providerName: "Microsoft",
      })
      break
    case "openIdConnect":
      schoolLoginProvider = new firebase.app.auth.OAuthProvider(orgOptions.openid_connect_provider_id)
      break
    case "saml":
      schoolLoginProvider = new firebase.app.auth.SAMLAuthProvider(orgOptions.saml_provider_id)
      break
  }

  // Pick the credential will the simplest user flow, prioritizing:
  // 1) an existing school credential
  // 2) a non-email/password credential
  let reauthProvider = null
  if (schoolEmail && !schoolEmailNeedsConfirmation) {
    reauthProvider = schoolLoginProvider
  } else if (authUser.providerData.some(p => p.providerId !== "password" && p.email === email)) {
    reauthProvider = authUser.providerData.find(p => p.providerId !== "password" && p.email === email)
  } else {
    reauthProvider = new firebase.app.auth.EmailAuthProvider()
  }

  return {
    schoolLoginProvider,
    reauthProvider,
    email,
    personalEmail,
    schoolEmail,
    schoolEmailNeedsConfirmation,
  }
}

export default useUserEmailCredentials
